import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type {
  CalculatorEmailHistoryItem,
  RecipientInfoEmail,
  SenderInfoEmail,
} from '../types/calculator-email.types';

export function useCalculatorEmailHistory() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [history, setHistory] = useState<CalculatorEmailHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_history')
        .select('*')
        .eq('user_id', user.id)
        .like('subject', '%Calculator Report%')
        .order('sent_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Filter to only items with mortgageSummary in loan_details
      const calculatorEmails = (data || []).filter((item) => {
        const loanDetails = item.loan_details as Record<string, unknown>;
        return loanDetails?.mortgageSummary;
      }) as unknown as CalculatorEmailHistoryItem[];

      setHistory(calculatorEmails);
    } catch (error) {
      console.error('Failed to fetch email history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const deleteEntry = useCallback(
    async (id: string) => {
      if (!user?.id) return;

      try {
        const { error } = await supabase
          .from('email_history')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) throw error;

        setHistory((prev) => prev.filter((item) => item.id !== id));
        toast({
          title: 'Entry deleted',
          description: 'Email history entry removed',
        });
      } catch (error) {
        console.error('Failed to delete email history entry:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete entry',
          variant: 'destructive',
        });
      }
    },
    [user?.id, toast]
  );

  const convertHistoryToFormData = useCallback(
    (
      item: CalculatorEmailHistoryItem
    ): { recipient: RecipientInfoEmail; sender: SenderInfoEmail } => {
      return {
        recipient: {
          name: item.recipient_name,
          email: item.recipient_email,
        },
        sender: {
          name: item.sender_info?.name || '',
          email: item.sender_info?.email || '',
          phone: item.sender_info?.phone || '',
          company: item.sender_info?.company || '',
          nmls: item.sender_info?.nmls || '',
        },
      };
    },
    []
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    isLoading,
    fetchHistory,
    deleteEntry,
    convertHistoryToFormData,
  };
}
